import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { fetchAppointmentAudio } from "../../services/appointments";

function formatTime(seconds = 0) {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function AudioPlayButton({
  conversationId,
  hasRecording = true,
  pending = false,
  fetchAudioFn = null,  // Optional custom fetch function, defaults to fetchAppointmentAudio
}) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);

  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const progress = useMemo(() => {
    if (!duration) return 0;
    return Math.min(100, Math.max(0, (currentTime / duration) * 100));
  }, [currentTime, duration]);

  const PLAYBACK_SPEEDS = [1, 1.25, 1.5];

  const cyclePlaybackSpeed = useCallback(() => {
    setPlaybackRate((prev) => {
      const currentIndex = PLAYBACK_SPEEDS.indexOf(prev);
      const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
      return PLAYBACK_SPEEDS[nextIndex];
    });
  }, []);

  // Apply playback rate to audio element
  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.playbackRate = playbackRate;
    }
  }, [playbackRate, audioUrl]);

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  };

  const tick = () => {
    const el = audioRef.current;
    if (!el) return;

    setCurrentTime(el.currentTime || 0);

    if (!el.paused && !el.ended) {
      rafRef.current = requestAnimationFrame(tick);
    } else {
      stopRaf();
    }
  };

  const togglePlayPause = useCallback(async () => {
    // ✅ hard stop if no recording
    if (!conversationId || !hasRecording || pending) return;

    const el = audioRef.current;

    // Already loaded → toggle
    if (audioUrl && el) {
      if (el.paused) {
        try {
          await el.play();
          setState("playing");
          stopRaf();
          rafRef.current = requestAnimationFrame(tick);
        } catch {
          setError("Failed to play audio");
          setState("error");
        }
      } else {
        el.pause();
        setState("paused");
        stopRaf();
      }
      return;
    }

    // Fetch URL first
    setState("loading");
    setError(null);

    try {
      const response = fetchAudioFn
        ? await fetchAudioFn()
        : await fetchAppointmentAudio(conversationId);
      const url = response?.data?.audio_url;
      if (!url) throw new Error("No audio URL received");
      setAudioUrl(url);
      setState("paused");
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to load audio";
      setError(message);
      setState("error");
    }
  }, [conversationId, audioUrl, hasRecording, pending, fetchAudioFn]);

  // Auto-play after src is set (still user-initiated from the click)
  useEffect(() => {
    const el = audioRef.current;
    if (!audioUrl || !el) return;

    const playAfterLoad = async () => {
      try {
        setState("paused");
        await el.play();
        setState("playing");
        stopRaf();
        rafRef.current = requestAnimationFrame(tick);
      } catch {
        setState("paused");
      }
    };

    playAfterLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  useEffect(() => {
    return () => stopRaf();
  }, []);

  const handleRetry = () => {
    stopRaf();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setAudioUrl(null);
    setDuration(0);
    setCurrentTime(0);
    setPlaybackRate(1);
    setError(null);
    setState("idle");
  };

  // ✅ If no recording (or pending), show a simple badge instead of the player
  if (!conversationId || !hasRecording || pending) {
    const label = pending ? "Processing…" : "No audio";
    const dot = pending ? "bg-amber-400" : "bg-gray-300";
    const text = pending ? "text-amber-700" : "text-gray-500";
    const bg = pending ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200";

    return (
      <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${bg} ${text}`}>
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        {label}
      </span>
    );
  }

  if (state === "error") {
    return (
      <div className="flex items-center gap-2">
        <span className="text-red-500 text-xs" title={error}>
          {error}
        </span>
        <button
          type="button"
          onClick={handleRetry}
          className="text-blue-600 hover:text-blue-700 text-xs underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div className="flex items-center gap-2">
        <svg className="h-4 w-4 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-gray-500 text-xs">Loading…</span>
      </div>
    );
  }

  const isPlaying = state === "playing";

  return (
    <div className="flex items-center gap-2 min-w-[180px] max-w-[220px]">
      <button
        type="button"
        onClick={togglePlayPause}
        className={`flex items-center justify-center h-8 w-8 rounded-full transition-colors shrink-0 ${
          isPlaying
            ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      <button
        type="button"
        onClick={cyclePlaybackSpeed}
        className="flex items-center justify-center h-6 px-1.5 rounded text-[10px] font-medium transition-colors shrink-0 bg-gray-100 text-gray-700 hover:bg-gray-200"
        title="Playback speed"
      >
        {playbackRate}x
      </button>

      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
          <div className="h-full bg-blue-500" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between text-[10px] text-gray-500 tabular-nums">
          <span>{formatTime(currentTime)}</span>
          <span>{duration ? formatTime(duration) : ""}</span>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={audioUrl || undefined}
        preload="metadata"
        onLoadedMetadata={() => {
          const el = audioRef.current;
          if (!el) return;
          setDuration(el.duration || 0);
        }}
        onTimeUpdate={() => {
          const el = audioRef.current;
          if (!el) return;
          setCurrentTime(el.currentTime || 0);
        }}
        onPlay={() => setState("playing")}
        onPause={() => setState("paused")}
        onEnded={() => {
          setState("paused");
          stopRaf();
        }}
        onError={() => {
          setError("Failed to play audio");
          setState("error");
          stopRaf();
        }}
      />
    </div>
  );
}
